/*
Ghost CMS Pagination Handler
Handles pagination navigation, URL management, and error handling
*/

class GhostPagination {
    constructor(options = {}) {
        this.currentPage = options.currentPage || 1;
        this.totalPages = options.totalPages || 1;
        this.basePath = options.basePath || '';
        this.container = options.container || '.pagination-container';
        this.loadingClass = 'pagination-loading';
        this.errorClass = 'pagination-error';
        this.initialized = false;
        this.isLoading = false;
        
        this.init();
    }

    init() {
        if (this.initialized) {
            console.log('Pagination already initialized, skipping...');
            return;
        }
        
        this.bindEvents();
        this.setupURLHandling();
        this.initialized = true;
        console.log('Pagination initialized successfully');
    }

    bindEvents() {
        // Remove existing event listeners to prevent duplicates
        this.removeEventListeners();
        
        // Handle pagination link clicks
        this.clickHandler = (e) => {
            if (e.target.closest('[data-pagination]')) {
                e.preventDefault();
                const link = e.target.closest('[data-pagination]');
                const page = link.dataset.page;
                this.navigateToPage(page);
            }
        };
        
        document.addEventListener('click', this.clickHandler);

        // Handle browser back/forward
        this.popstateHandler = (e) => {
            if (e.state && e.state.page) {
                this.loadPage(e.state.page, false);
            }
        };
        
        window.addEventListener('popstate', this.popstateHandler);
    }

    removeEventListeners() {
        if (this.clickHandler) {
            document.removeEventListener('click', this.clickHandler);
        }
        if (this.popstateHandler) {
            window.removeEventListener('popstate', this.popstateHandler);
        }
    }

    rebindEvents() {
        // Just rebind the click events for new pagination links
        // Don't re-initialize the entire pagination
        console.log('Rebinding pagination events');
    }

    setupURLHandling() {
        // Update URL without page reload
        if (window.history && window.history.pushState) {
            const currentPage = this.getCurrentPageFromURL();
            if (currentPage && currentPage !== this.currentPage) {
                this.currentPage = currentPage;
            }
        }
    }

    getCurrentPageFromURL() {
        const url = window.location.pathname;
        const pageMatch = url.match(/\/page\/(\d+)/);
        return pageMatch ? parseInt(pageMatch[1]) : 1;
    }

    navigateToPage(page) {
        if (page < 1 || page > this.totalPages) {
            this.showError('Invalid page number');
            return;
        }

        // Prevent multiple rapid clicks
        if (this.isLoading) {
            console.log('Already loading, ignoring click');
            return;
        }

        // Check if we should use AJAX or fallback to regular navigation
        const useAJAX = this.shouldUseAJAX();
        
        if (!useAJAX) {
            // Fallback to regular navigation
            const url = this.buildPageURL(page);
            window.location.href = url;
            return;
        }

        this.isLoading = true;
        this.showLoading();
        
        // Update URL
        this.updateURL(page);
        
        // Load page content
        this.loadPage(page);
    }

    shouldUseAJAX() {
        // Always use AJAX for pagination
        return true;
    }

    updateURL(page) {
        const newURL = this.buildPageURL(page);
        
        if (window.history && window.history.pushState) {
            window.history.pushState(
                { page: page },
                `Page ${page}`,
                newURL
            );
        }
    }

    async loadPage(page, updateHistory = true) {
        try {
            const url = this.buildPageURL(page);
            console.log('Loading page URL:', url); // Debug log
            
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            // Extract posts
            const postsContainer = document.querySelector('.gh-postfeed');
            const newPostsContainer = doc.querySelector('.gh-postfeed');
            
            if (postsContainer && newPostsContainer) {
                // Smoothly fade out current content
                postsContainer.style.opacity = '0.5';
                postsContainer.style.transition = 'opacity 0.3s ease';
                
                setTimeout(() => {
                    postsContainer.innerHTML = newPostsContainer.innerHTML;
                    postsContainer.style.opacity = '1';
                }, 300);
            }

            // Update pagination
            const newPagination = doc.querySelector('.pagination-container');
            const currentPagination = document.querySelector('.pagination-container');
            
            if (currentPagination && newPagination) {
                currentPagination.innerHTML = newPagination.innerHTML;
                
                // Re-bind events for new pagination links
                this.rebindEvents();
            }

            // Update page title
            const newTitle = doc.querySelector('title');
            if (newTitle) {
                document.title = newTitle.textContent;
            }

            // Update current page
            this.currentPage = page;
            
            // Scroll to top smoothly
            window.scrollTo({ top: 0, behavior: 'smooth' });
            
            this.hideLoading();
            this.isLoading = false;
            
            // Trigger custom event
            this.triggerPageChangeEvent(page);
            
            // Re-initialize any other components that might need it
            this.reinitializeComponents();
            
        } catch (error) {
            console.error('Pagination error:', error);
            this.showError('Failed to load page');
            this.hideLoading();
            this.isLoading = false;
        }
    }

    reinitializeComponents() {
        // Re-initialize search functionality
        if (window.ghostSearch) {
            window.ghostSearch.reinitialize();
        }
        
        // Re-initialize any other components
        if (window.infiniteScroll) {
            // Re-initialize infinite scroll if it exists
            window.infiniteScroll();
        }
    }

    buildPageURL(page) {
        const currentURL = window.location.pathname;
        
        // Handle the /blog/ URL structure
        if (currentURL.includes('/blog/')) {
            // If we're on /blog/ or /blog/page/X/, extract the base
            let baseURL = currentURL.replace(/\/page\/\d+/, ''); // Remove existing page number
            baseURL = baseURL.replace(/\/+$/, ''); // Remove trailing slashes
            
            if (page > 1) {
                return `${baseURL}/page/${page}/`;
            } else {
                return `${baseURL}/`;
            }
        } else {
            // Handle root URL structure
            let baseURL = currentURL.replace(/\/page\/\d+/, '');
            baseURL = baseURL.replace(/\/+$/, '');
            
            if (page > 1) {
                return `${baseURL}/page/${page}/`;
            } else {
                return `${baseURL}/`;
            }
        }
    }

    showLoading() {
        const container = document.querySelector(this.container);
        if (container) {
            container.classList.add(this.loadingClass);
            
            // Add loading indicator
            const loadingEl = document.createElement('div');
            loadingEl.className = 'pagination-loading-indicator';
            loadingEl.innerHTML = `
                <div class="flex items-center justify-center py-4">
                    <svg class="animate-spin h-5 w-5 text-ag-blue" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span class="ml-2 text-sm text-gray-400">Loading...</span>
                </div>
            `;
            container.appendChild(loadingEl);
        }
    }

    hideLoading() {
        const container = document.querySelector(this.container);
        if (container) {
            container.classList.remove(this.loadingClass);
            
            // Remove loading indicator
            const loadingEl = container.querySelector('.pagination-loading-indicator');
            if (loadingEl) {
                loadingEl.remove();
            }
        }
    }

    showError(message) {
        const container = document.querySelector(this.container);
        if (container) {
            container.classList.add(this.errorClass);
            
            // Add error message
            const errorEl = document.createElement('div');
            errorEl.className = 'pagination-error-message';
            errorEl.innerHTML = `
                <div class="flex items-center justify-center py-4 text-red-400">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span class="text-sm">${message}</span>
                </div>
            `;
            container.appendChild(errorEl);
            
            // Remove error after 3 seconds
            setTimeout(() => {
                container.classList.remove(this.errorClass);
                errorEl.remove();
            }, 3000);
        }
    }

    triggerPageChangeEvent(page) {
        const event = new CustomEvent('ghostPageChange', {
            detail: {
                page: page,
                totalPages: this.totalPages
            }
        });
        document.dispatchEvent(event);
    }

    // Public methods
    getCurrentPage() {
        return this.currentPage;
    }

    getTotalPages() {
        return this.totalPages;
    }

    setTotalPages(total) {
        this.totalPages = total;
    }
}

// Initialize pagination if available
document.addEventListener('DOMContentLoaded', function() {
    // Prevent multiple initializations
    if (window.ghostPagination) {
        console.log('Pagination already exists, skipping initialization');
        return;
    }
    
    const paginationContainer = document.querySelector('.pagination-container');
    
    if (paginationContainer) {
        // Extract pagination data from the page
        const currentPage = parseInt(paginationContainer.dataset.currentPage) || 1;
        const totalPages = parseInt(paginationContainer.dataset.totalPages) || 1;
        
        // Initialize pagination
        window.ghostPagination = new GhostPagination({
            currentPage: currentPage,
            totalPages: totalPages,
            container: '.pagination-container'
        });
    }
});

export default GhostPagination; 